import React, { useContext, FunctionComponent, useState, useCallback, useEffect } from 'react';

export interface IModalCoords {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

interface IModalInterface {
  modalCoords: IModalCoords;
  updateModalCoords: ((newCoords: IModalCoords) => (() => void)) | null;
}

const defaultCoords = {
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
};

const ModalCoordsContext = React.createContext<IModalInterface>({
  modalCoords: defaultCoords,
  updateModalCoords: null,
});

export const useConstrainModalArea = ({
  top = 0,
  bottom = 0,
  left = 0,
  right = 0,
}: Partial<IModalCoords>) => {
  const { updateModalCoords } = useContext(ModalCoordsContext);

  useEffect(() => {
    if (updateModalCoords) {
      const release = updateModalCoords({ top, bottom, left, right });

      return () => {
        if (release) {
          release();
        }
      };
    }
  }, [updateModalCoords, top, bottom, left, right]);
};

export const useModalCoords = () => useContext(ModalCoordsContext).modalCoords;

export const ModalCoordsProvider: FunctionComponent = ({ children }) => {
  const [coords, setCoords] = useState<IModalCoords>(defaultCoords);

  const updateModalCoords = useCallback((newCoords: IModalCoords) => {
    const { top, bottom, left, right } = newCoords;
    setCoords((prev) => ({
      top: prev.top + top,
      bottom: prev.bottom + bottom,
      left: prev.left + left,
      right: prev.right + right,
    }));

    return () => {
      setCoords((prev) => ({
        top: prev.top - top,
        right: prev.right - right,
        left: prev.left - left,
        bottom: prev.bottom - bottom,
      }));
    };
  }, [setCoords]);

  return (
    <ModalCoordsContext.Provider value={{
      modalCoords: coords,
      updateModalCoords,
    }}
    >
      {children}
    </ModalCoordsContext.Provider>
  );
};
